import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useApp } from '@/context/AppContext';
import { clientApi } from '@/lib/clientApi';
import { OnboardingWizard } from '@/components/client/OnboardingWizard';
import { ProjectWorkspace } from '@/components/client/ProjectWorkspace';
import { FileUploadZone } from '@/components/FileUploadZone';
import { CommentThread } from '@/components/CommentThread';
import {
  CheckCircle2, Circle, ArrowRight, ArrowLeft, LogOut, Loader2, Plus,
  LayoutGrid, Settings, Briefcase, Archive, ChevronRight, Sparkles,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* Client-side mirror of backend state labels (display only). */
const STATE_LABELS = {
  draft: 'Getting started', requirements_submitted: 'Requirements submitted', scoping: 'Scope in review',
  scope_approved: 'Scope approved', agreement_pending: 'Agreement sent', agreement_accepted: 'Awaiting payment',
  subscribed: 'Subscribed', onboarding: 'Onboarding', in_development: 'In development', launched: 'Launched',
  ongoing: 'Active & managed', paused: 'Paused', cancelled: 'Cancelled', legacy: 'Legacy',
};
const STATE_TONE = {
  draft: 'bg-slate-100 text-slate-600', requirements_submitted: 'bg-brand-blue/10 text-brand-blue',
  scoping: 'bg-amber-100 text-amber-700', scope_approved: 'bg-brand-blue/10 text-brand-blue',
  agreement_pending: 'bg-amber-100 text-amber-700', agreement_accepted: 'bg-brand-orange/10 text-brand-orange',
  subscribed: 'bg-green-100 text-green-700', onboarding: 'bg-brand-blue/10 text-brand-blue',
  in_development: 'bg-brand-blue/10 text-brand-blue', launched: 'bg-green-100 text-green-700',
  ongoing: 'bg-green-100 text-green-700', paused: 'bg-slate-100 text-slate-600',
  cancelled: 'bg-rose-100 text-rose-700', legacy: 'bg-slate-100 text-slate-600',
};

/* ── Legacy order progress tracker (preserves historical records) ── */
const LegacyPhaseTracker = ({ phases }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-0" data-testid="legacy-phase-tracker">
    {(phases || []).map((phase, i) => (
      <React.Fragment key={phase.id}>
        <div className="flex sm:flex-col items-center gap-2 sm:flex-1">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 ${phase.done ? 'bg-green-50 border-green-400' : 'bg-secondary/60 border-[color:var(--hairline)]'}`}>
            {phase.done ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Circle className="w-4 h-4 text-muted-foreground/40" />}
          </div>
          <p className={`text-[11px] font-medium sm:text-center sm:max-w-[100px] leading-tight ${phase.done ? 'text-foreground' : 'text-muted-foreground'}`}>{phase.label}</p>
        </div>
        {i < phases.length - 1 && <div className={`hidden sm:block h-0.5 flex-1 -mt-5 ${phase.done ? 'bg-green-400' : 'bg-border'}`} />}
      </React.Fragment>
    ))}
  </div>
);

const LegacyOrderWorkspace = ({ order, onBack, user }) => {
  const [assets, setAssets] = useState([]);
  const refresh = useCallback(async () => {
    try { const r = await axios.get(`${API}/orders/${order.order_id}`); setAssets(r.data.assets || []); } catch (e) { /* non-critical */ }
  }, [order.order_id]);
  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div data-testid="legacy-workspace">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-slate-900 transition-colors mb-3"><ArrowLeft className="w-4 h-4" /> All projects</button>
      <div className="flex items-center gap-2 mb-1">
        <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">{order.gig_title || 'Project'}</h1>
        <span className="text-[11px] font-bold px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 uppercase tracking-wide">Legacy</span>
      </div>
      <p className="text-sm text-muted-foreground mb-6">{order.tier_name} · Order {order.order_id} · Status {order.status}</p>

      <div className="bg-white rounded-2xl border border-[color:var(--hairline)] shadow-[var(--shadow-soft)] p-5 sm:p-7 mb-6">
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-5">Project Progress</h3>
        <LegacyPhaseTracker phases={order.phases || []} />
      </div>

      {(order.deliverables || []).length > 0 && (
        <div className="bg-white rounded-2xl border border-[color:var(--hairline)] shadow-[var(--shadow-soft)] p-5 sm:p-7 mb-6">
          <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-4">Deliverables</h3>
          <div className="space-y-2.5">
            {order.deliverables.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-xl border border-[color:var(--hairline)] p-3">
                <span className="text-sm text-slate-800">{d.title}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md ${d.status === 'approved' ? 'bg-green-50 text-green-600' : d.status === 'revision' ? 'bg-brand-orange/10 text-brand-orange' : 'bg-secondary text-slate-500'}`}>{d.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[color:var(--hairline)] shadow-[var(--shadow-soft)] p-5 sm:p-7 mb-6">
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-4">Project Assets</h3>
        <FileUploadZone orderId={order.order_id} assets={assets} onUploadComplete={refresh} onDelete={async (id) => { try { await axios.delete(`${API}/orders/${order.order_id}/assets/${id}`); setAssets((p) => p.filter((a) => a.id !== id)); } catch (e) { /* ignore */ } }} />
      </div>

      <div className="bg-white rounded-2xl border border-[color:var(--hairline)] shadow-[var(--shadow-soft)] p-5 sm:p-7">
        <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-4">Project Updates</h3>
        <CommentThread orderId={order.order_id} currentUser="client" currentName={user?.name || 'Client'} />
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   CLIENT PORTAL — multi-project, DaaS-first
   ═══════════════════════════════════════════ */
export default function ClientDashboard() {
  const { user, setUser, plans } = useApp();
  const navigate = useNavigate();

  const [sessionRestored, setSessionRestored] = useState(false);
  const [account, setAccount] = useState(null);
  const [projects, setProjects] = useState([]);
  const [orders, setOrders] = useState([]); // legacy
  const [view, setView] = useState('list'); // list | onboarding | workspace | legacy | settings
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [onboardingProject, setOnboardingProject] = useState(null); // draft to resume (or null = fresh)
  const [selectedOrder, setSelectedOrder] = useState(null);
  // Plan/cycle intent captured on the Services page → preselects onboarding.
  const [intent, setIntent] = useState(null);
  const intentConsumed = useRef(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('ml_daas_intent');
      if (raw) {
        const parsed = JSON.parse(raw);
        const fresh = parsed && (!parsed.savedAt || Date.now() - parsed.savedAt < 48 * 60 * 60 * 1000);
        if (fresh && parsed.plan_id) setIntent(parsed);
        else localStorage.removeItem('ml_daas_intent');
      }
    } catch (e) { /* noop */ }
  }, []);

  const loadData = useCallback(async () => {
    const [me, legacy] = await Promise.allSettled([clientApi.me(), clientApi.legacyMe()]);
    if (me.status === 'fulfilled') { setAccount(me.value.account); setProjects(me.value.projects || []); }
    if (legacy.status === 'fulfilled') {
      setOrders(legacy.value.orders || []);
      if (legacy.value.user && !user) setUser({ name: legacy.value.user.name || legacy.value.user.email, email: legacy.value.user.email });
    }
  }, [user, setUser]);

  useEffect(() => {
    const token = sessionStorage.getItem('ml_client_token');
    if (!token) { setSessionRestored(true); return; }
    loadData().finally(() => setSessionRestored(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = () => { sessionStorage.removeItem('ml_client_token'); setUser(null); navigate('/auth'); };

  // Auto-open onboarding (once) with the plan + billing cycle chosen on Services.
  useEffect(() => {
    if (intentConsumed.current) return;
    if (sessionRestored && sessionStorage.getItem('ml_client_token') && intent) {
      intentConsumed.current = true;
      try { localStorage.removeItem('ml_daas_intent'); } catch (e) { /* noop */ }
      setOnboardingProject(null);
      setView('onboarding');
    }
  }, [sessionRestored, intent]);

  // Non-legacy projects (legacy orders are shown separately from legacy "projects" to avoid duplication).
  const daasProjects = projects.filter((p) => p.lifecycle_state !== 'legacy');
  const legacyProjects = projects.filter((p) => p.lifecycle_state === 'legacy');

  const openProject = (p) => {
    const draftIncomplete = p.lifecycle_state === 'draft' && p.requirements_status !== 'submitted';
    if (draftIncomplete) { setOnboardingProject(p); setView('onboarding'); }
    else { setSelectedProjectId(p.id); setView('workspace'); }
  };

  const openLegacyOrder = (order) => { setSelectedOrder(order); setView('legacy'); };

  if (!sessionRestored) {
    return <div className="min-h-screen bg-background flex items-center justify-center"><Loader2 className="w-8 h-8 text-brand-blue animate-spin" /></div>;
  }

  if (!sessionStorage.getItem('ml_client_token')) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-bold text-foreground mb-2">Please sign in</h2>
          <p className="text-sm text-muted-foreground mb-4">Sign in to access your project portal.</p>
          <Link to="/auth" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-semibold hover:bg-brand-blue-dark transition-colors" data-testid="goto-signin">Sign In <ArrowRight className="w-4 h-4" /></Link>
        </div>
      </div>
    );
  }

  const totalCount = daasProjects.length + legacyProjects.length + orders.length;

  return (
    <div className="min-h-screen bg-background" data-testid="client-dashboard-page">
      <div className="pt-28 sm:pt-32 lg:pt-36 pb-16 sm:pb-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{user?.name ? `Welcome, ${user.name}` : 'Client Portal'}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Your projects, all managed in one place.</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setView(view === 'settings' ? 'list' : 'settings')} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-[color:var(--hairline)] text-xs font-semibold text-slate-600 hover:bg-secondary/60 transition-colors" data-testid="settings-btn"><Settings className="w-3.5 h-3.5" /> Settings</button>
              <button onClick={handleLogout} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-red-200 text-xs font-semibold text-red-500 hover:bg-red-50 transition-colors" data-testid="logout-btn"><LogOut className="w-3.5 h-3.5" /> Log Out</button>
            </div>
          </div>

          <AnimatePresence mode="wait">
            {/* ─────────── LIST ─────────── */}
            {view === 'list' && (
              <motion.div key="list" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <div className="flex items-center justify-between mb-5">
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2"><LayoutGrid className="w-5 h-5 text-brand-blue" /> Your Projects</h2>
                  <button onClick={() => { setOnboardingProject(null); setView('onboarding'); }} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold shadow-md shadow-brand-blue/15 hover:bg-brand-blue-dark transition-colors" data-testid="new-project-btn"><Plus className="w-4 h-4" /> New project</button>
                </div>

                {totalCount === 0 ? (
                  <div className="text-center py-16 bg-white rounded-2xl border border-[color:var(--hairline)]" data-testid="empty-projects">
                    <div className="w-14 h-14 rounded-2xl bg-brand-blue/10 flex items-center justify-center mx-auto mb-4"><Sparkles className="w-7 h-7 text-brand-blue" /></div>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">Start your first project</h3>
                    <p className="text-sm text-muted-foreground mb-6 max-w-md mx-auto">Tell us what you want to build — we&apos;ll scope it, build it and manage it on a simple subscription.</p>
                    <button onClick={() => { setOnboardingProject(null); setView('onboarding'); }} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-blue text-white text-sm font-bold hover:bg-brand-blue-dark transition-colors" data-testid="empty-start-btn"><Plus className="w-4 h-4" /> New project</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="project-list">
                    {daasProjects.map((p) => {
                      const plan = (plans || []).find((pl) => pl.id === p.plan_id);
                      const draftIncomplete = p.lifecycle_state === 'draft' && p.requirements_status !== 'submitted';
                      return (
                        <motion.button
                          key={p.id} onClick={() => openProject(p)} whileHover={{ y: -2 }}
                          className="text-left bg-white rounded-2xl border border-[color:var(--hairline)] p-5 hover:border-brand-blue/30 hover:shadow-[var(--shadow-lift)] transition-all"
                          data-testid={`project-card-${p.id}`}
                        >
                          <div className="flex items-start justify-between gap-2 mb-3">
                            <div className="w-10 h-10 rounded-xl bg-brand-blue/10 flex items-center justify-center"><Briefcase className="w-5 h-5 text-brand-blue" /></div>
                            <span className={`text-[10px] font-bold px-2 py-1 rounded-md ${STATE_TONE[p.lifecycle_state] || 'bg-slate-100 text-slate-600'}`}>{STATE_LABELS[p.lifecycle_state] || p.lifecycle_state}</span>
                          </div>
                          <h3 className="text-base font-bold text-slate-900 truncate">{p.name}</h3>
                          <p className="text-xs text-muted-foreground mt-0.5">{plan?.name || p.plan_id || 'No plan'}</p>
                          <div className="flex items-center justify-between mt-4 pt-3 border-t border-[color:var(--hairline)]">
                            <span className={`text-xs font-semibold ${draftIncomplete ? 'text-brand-orange' : 'text-brand-blue'}`}>{draftIncomplete ? 'Continue setup' : 'Open project'}</span>
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          </div>
                        </motion.button>
                      );
                    })}

                    {orders.map((o) => (
                      <motion.button
                        key={o.order_id} onClick={() => openLegacyOrder(o)} whileHover={{ y: -2 }}
                        className="text-left bg-white rounded-2xl border border-[color:var(--hairline)] p-5 hover:border-brand-blue/30 hover:shadow-[var(--shadow-lift)] transition-all"
                        data-testid={`legacy-card-${o.order_id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center"><Archive className="w-5 h-5 text-slate-500" /></div>
                          <span className="text-[10px] font-bold px-2 py-1 rounded-md bg-slate-100 text-slate-600">Legacy</span>
                        </div>
                        <h3 className="text-base font-bold text-slate-900 truncate">{o.gig_title || 'Project'}</h3>
                        <p className="text-xs text-muted-foreground mt-0.5">{o.tier_name} · {o.status}</p>
                        <div className="flex items-center justify-between mt-4 pt-3 border-t border-[color:var(--hairline)]">
                          <span className="text-xs font-semibold text-brand-blue">View history</span>
                          <ChevronRight className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </motion.button>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {/* ─────────── WORKSPACE (DaaS) ─────────── */}
            {view === 'workspace' && selectedProjectId && (
              <motion.div key="workspace" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <ProjectWorkspace projectId={selectedProjectId} plans={plans} onBack={() => { setView('list'); setSelectedProjectId(null); loadData(); }} onChanged={loadData} />
              </motion.div>
            )}

            {/* ─────────── LEGACY ─────────── */}
            {view === 'legacy' && selectedOrder && (
              <motion.div key="legacy" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <LegacyOrderWorkspace order={selectedOrder} user={user} onBack={() => { setView('list'); setSelectedOrder(null); }} />
              </motion.div>
            )}

            {/* ─────────── SETTINGS ─────────── */}
            {view === 'settings' && (
              <motion.div key="settings" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                <button onClick={() => setView('list')} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-slate-900 transition-colors mb-4"><ArrowLeft className="w-4 h-4" /> Back</button>
                <div className="max-w-lg">
                  <h3 className="text-lg font-bold text-slate-900 mb-6">Account Settings</h3>
                  <div className="bg-white rounded-2xl border border-[color:var(--hairline)] p-6 space-y-4">
                    <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Email Address</label><p className="text-sm text-slate-900 font-medium" data-testid="settings-email">{user?.email || account?.email || '—'}</p></div>
                    <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Name</label><p className="text-sm text-slate-900 font-medium">{user?.name || account?.name || '—'}</p></div>
                    <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Projects</label><p className="text-sm text-slate-600">{daasProjects.length} active · {legacyProjects.length + orders.length} legacy</p></div>
                    <div className="pt-4 border-t border-[color:var(--hairline)]"><button onClick={handleLogout} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold text-red-500 border border-red-200 hover:bg-red-50 transition-colors"><LogOut className="w-4 h-4" /> Sign Out</button></div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Onboarding wizard (overlay) */}
      {view === 'onboarding' && (
        <OnboardingWizard
          plans={plans}
          existingProject={onboardingProject}
          initialPlanId={intent?.plan_id}
          initialCycle={intent?.cycle}
          onClose={() => { setView('list'); setOnboardingProject(null); loadData(); }}
          onDone={(res) => {
            loadData();
            if (res?.stay && res.projectId) { setSelectedProjectId(res.projectId); setView('workspace'); }
            else { setView('list'); }
            setOnboardingProject(null);
          }}
        />
      )}
    </div>
  );
}
